'use strict';

import { Vector2, Vector3, Matrix4, Color, Quaternion } from 'three';
import { Color4 } from '../types/color4';

export class BinaryReader {
  private buffer: Uint8Array;
  private view: DataView;
  public pos: number;

  get byteLength(): number {
    return this.buffer.byteLength;
  }

  constructor(arrayBuffer: ArrayBuffer) {
    this.buffer = new Uint8Array(arrayBuffer);
    this.view = new DataView(arrayBuffer);
    this.pos = 0;
  }

  readFloat(): number {
    const res = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return res;
  }

  readUint8(): number {
    return this.buffer[this.pos++];
  }

  readUint16(): number {
    const res = this.buffer[this.pos + 1] << 8 |
                this.buffer[this.pos + 0];
    this.pos += 2;
    return res;
  }

  readUint24(): number {
    const res = this.buffer[this.pos + 2] << 16 |
                this.buffer[this.pos + 1] << 8 |
                this.buffer[this.pos + 0];
    this.pos += 3;
    return res;
  }

  readUint32(): number {
    const res = (this.buffer[this.pos + 3] << 24 |
                 this.buffer[this.pos + 2] << 16 |
                 this.buffer[this.pos + 1] << 8 |
                 this.buffer[this.pos + 0]) >>> 0;
    this.pos += 4;
    return res;
  }

  readInt8(): number {
    const res = this.view.getInt8(this.pos);
    this.pos += 1;
    return res;
  }

  readInt16(): number {
    const res = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return res;
  }

  readInt32(): number {
    const res = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return res;
  }

  readStr(): string {
    const startPos = this.pos;
    while (this.buffer[this.pos++]);
    const strArray = this.buffer.subarray(startPos, this.pos - 1);
    return String.fromCharCode.apply(null, strArray as any);
  }

  readStrLen(len: number): string {
    let realStrLen: number;
    for (realStrLen = 0; realStrLen < len; realStrLen++) {
      if (this.buffer[this.pos + realStrLen] === 0) break;
    }
    const strArray = this.buffer.subarray(this.pos, this.pos + realStrLen);
    this.pos += len;
    return String.fromCharCode.apply(null, strArray as any);
  }

  readUint8Array(len: number): Uint8Array {
    const array = new Uint8Array(this.buffer.buffer, this.pos, len);
    this.pos += len;
    return array;
  }

  slice(start: number, end: number = this.buffer.byteLength): Uint8Array {
    if (start < 0 || end < start || end > this.buffer.byteLength) {
      throw new RangeError(`Invalid binary slice ${start}..${end}`);
    }
    return this.buffer.slice(start, end);
  }

  readUint16Array(len: number): Uint16Array {
    if ((this.pos % 2) === 0) {
      const array = new Uint16Array(this.buffer.buffer, this.pos, len);
      this.pos += len * 2;
      return array;
    } else {
      const buffer = new Uint8Array(this.readUint8Array(len * 2));
      return new Uint16Array(buffer.buffer, 0, len);
    }
  }

  readUint32Array(len: number): Uint32Array {
    if ((this.pos % 4) === 0) {
      const array = new Uint32Array(this.buffer.buffer, this.pos, len);
      this.pos += len * 4;
      return array;
    } else {
      const buffer = new Uint8Array(this.readUint8Array(len * 4));
      return new Uint32Array(buffer.buffer, 0, len);
    }
  }

  readFloatArray(len: number): Float32Array {
    if ((this.pos % 4) === 0) {
      const array = new Float32Array(this.buffer.buffer, this.pos, len);
      this.pos += len * 4;
      return array;
    } else {
      const buffer = new Uint8Array(this.readUint8Array(len * 4));
      return new Float32Array(buffer.buffer, 0, len);
    }
  }

  readUint8Str(): string {
    return this.readStrLen(this.readUint8());
  }

  readUint16Str(): string {
    return this.readStrLen(this.readUint16());
  }

  readUint32Str(): string {
    return this.readStrLen(this.readUint32());
  }

  readVarLengthStr(): string {
    let chr = this.readUint8();
    let length = chr & 0x7f;
    let shift = 7;

    while (chr & 0x80) {
      chr = this.readUint8();
      length |= (chr & 0x7f) << shift;
      shift += 7;
    }

    return this.readStrLen(length);
  }

  tell(): number {
    return this.pos;
  }

  seek(pos: number): void {
    if (pos >= 0) {
      this.pos = pos;
    } else {
      this.pos = this.buffer.byteLength + pos;
    }
  }

  skip(num: number): void {
    this.pos += num;
  }

  readIntVector2(): Vector2 {
    const x = this.readInt32();
    const y = this.readInt32();
    return new Vector2(x, y);
  }

  readVector2(): Vector2 {
    const x = this.readFloat();
    const y = this.readFloat();
    return new Vector2(x, y);
  }

  readVector3(): Vector3 {
    const x = this.readFloat();
    const y = this.readFloat();
    const z = this.readFloat();
    return new Vector3(x, y, z);
  }

  readVector3xzy(): Vector3 {
    const x = this.readFloat();
    const z = this.readFloat();
    const y = this.readFloat();
    return new Vector3(x, y, z);
  }

  readMatrix4(): Matrix4 {
    const out = new Matrix4();
    for (let i = 0; i < 16; ++i) {
      out.elements[i] = this.readFloat();
    }
    return out;
  }

  readColour(): Color {
    const r = this.readFloat();
    const g = this.readFloat();
    const b = this.readFloat();
    return new Color(r, g, b);
  }

  readColor4(): Color4 {
    const r = this.readFloat();
    const g = this.readFloat();
    const b = this.readFloat();
    const a = this.readFloat();
    return new Color4(r, g, b, a);
  }

  readQuat(): Quaternion {
    const x = this.readFloat();
    const y = this.readFloat();
    const z = this.readFloat();
    const w = this.readFloat();
    return new Quaternion(x, y, z, w);
  }

  readQuatwxyz(): Quaternion {
    const w = this.readFloat();
    const x = this.readFloat();
    const y = this.readFloat();
    const z = this.readFloat();
    return new Quaternion(x, y, z, w);
  }
}
